import * as DialogPrimitive from '@radix-ui/react-dialog';
import { type ReactNode, forwardRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

export const MobileDrawer = DialogPrimitive.Root;
export const MobileDrawerTrigger = DialogPrimitive.Trigger;
export const MobileDrawerPortal = DialogPrimitive.Portal;
export const MobileDrawerClose = DialogPrimitive.Close;

function VisuallyHidden(props: { children: ReactNode }) {
  return (
    <span
      style={{
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: '0',
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: '0',
      }}
    >
      {props.children}
    </span>
  );
}

export const MobileDrawerOverlay = forwardRef<HTMLDivElement, { className?: string }>(
  function MobileDrawerOverlay(props, ref) {
    return (
      <DialogPrimitive.Overlay
        ref={ref}
        className={twMerge('fixed inset-0 z-50 bg-black/50 transition-opacity', props.className)}
      />
    );
  }
);
MobileDrawerOverlay.displayName = 'MobileDrawerOverlay';

type MobileDrawerContentProps = {
  children: ReactNode;
  className?: string;
  onNavigate?: () => void;
};

export function MobileDrawerContent(props: MobileDrawerContentProps) {
  const { children, className } = props;

  return (
    <MobileDrawerPortal>
      <MobileDrawerOverlay />
      <DialogPrimitive.Content
        className={twMerge(
          'fixed left-0 top-0 z-50 h-full w-[280px] bg-neutral-50 shadow-xl',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
          'data-[state=closed]:duration-300 data-[state=open]:duration-300',
          className
        )}
        aria-describedby={undefined}
      >
        <VisuallyHidden>
          <DialogPrimitive.Title>Navigation Menu</DialogPrimitive.Title>
        </VisuallyHidden>
        {children}
      </DialogPrimitive.Content>
    </MobileDrawerPortal>
  );
}

type MobileDrawerWithAutoCloseProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
  pathname: string;
};

export function MobileDrawerWithAutoClose(props: MobileDrawerWithAutoCloseProps) {
  const { open, onOpenChange, children, pathname } = props;

  useEffect(
    function () {
      if (open) {
        onOpenChange(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname]
  );

  return (
    <MobileDrawer open={open} onOpenChange={onOpenChange}>
      <MobileDrawerContent>{children}</MobileDrawerContent>
    </MobileDrawer>
  );
}
